import { useState, useCallback } from 'react';
import axios from 'axios';
import { NETLIFY_FUNCTIONS_BASE } from '../../config/api';

/**
 * Hook pour gérer les transactions bancaires
 *
 * Usage:
 * const {
 *   transactions,
 *   loading,
 *   error,
 *   fetchTransactions
 * } = useBankTransactions();
 */

export function useBankTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Récupère les transactions bancaires
   *
   * @param {Object} filters - Filtres optionnels
   * @param {string} filters.accountId - ID du compte bancaire
   * @param {string} filters.connectionId - ID de la connexion bancaire
   * @param {string} filters.startDate - Date de début (YYYY-MM-DD)
   * @param {string} filters.endDate - Date de fin (YYYY-MM-DD)
   * @returns {Promise<Array>} Liste des transactions
   */
  const fetchTransactions = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      // Construire les query params
      const params = new URLSearchParams();
      if (filters.accountId) params.append('accountId', filters.accountId);
      if (filters.connectionId) params.append('connectionId', filters.connectionId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const url = `${NETLIFY_FUNCTIONS_BASE}/getBankTransactions${params.toString() ? '?' + params.toString() : ''}`;

      console.log('[useBankTransactions] Requête:', url);

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('[useBankTransactions] Réponse:', response.data);

      setTransactions(response.data.transactions || []);
      return response.data.transactions || [];

    } catch (err) {
      console.error('Erreur lors de la récupération des transactions:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la récupération des transactions';
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Réinitialise les transactions
   */
  const clearTransactions = useCallback(() => {
    setTransactions([]);
    setError(null);
  }, []);

  return {
    transactions,
    loading,
    error,
    fetchTransactions,
    clearTransactions
  };
}

export default useBankTransactions;
